import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  TablePagination,
  Tooltip as MuiTooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { fetchHedwigAnalysis } from '../services/reliabilityService';
import { analyzeAlerts, AIAnalysisResult } from '../services/aiAnalysisService';
import { PDFExportService } from '../services/pdfExportService';
import AIInsightsDisplay from './AIInsightsDisplay';

import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  ExpandMore,
  Analytics,
  SmartToy,
  FilterList,
  PlayArrow,
  Info,
  Psychology,
  Close,
  PictureAsPdf,
  Share,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';

interface AlertData {
  timestamp: string;
  alertName: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Active' | 'Resolved' | 'Acknowledged';
  duration: number;
  source: string;
  signalToNoiseRatio: number;
}

interface AIInsight {
  type: 'trend' | 'anomaly' | 'recommendation';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
}

const SignalToNoiseRatio: React.FC = () => {
  // Analysis configuration states
  const [analysisType, setAnalysisType] = useState('hedwig-scope');
  const [hedwigScope, setHedwigScope] = useState('');
  const [appId, setAppId] = useState('');
  const [channel, setChannel] = useState('all');
  const [startTime, setStartTime] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  ); // 30 days ago
  const [endTime, setEndTime] = useState<string>(
    new Date().toISOString().slice(0, 16)
  ); // Now
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hedwigData, setHedwigData] = useState<any>(null);
  const [alertData, setAlertData] = useState<AlertData[]>([]);
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [trackingType, setTrackingType] = useState<'day' | 'week' | 'month'>('day');
  const [alertTypePage, setAlertTypePage] = useState(0);
  const [alertTypeRowsPerPage, setAlertTypeRowsPerPage] = useState(10);
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  
  // Tab state for organizing analysis sections
  const [activeTab, setActiveTab] = useState(0);
  
  // AI Analysis states
  const [aiAnalysisResult, setAiAnalysisResult] = useState<AIAnalysisResult | null>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);
  
  // PDF Export states
  const [pdfExportLoading, setPdfExportLoading] = useState(false);

  // Share link Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Parse URL parameters on mount to restore filter state
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    const hedwigScopeParam = urlParams.get('hedwigScope');
    const channelParam = urlParams.get('channel');
    const startTimeParam = urlParams.get('startTime');
    const endTimeParam = urlParams.get('endTime');
    const analysisTypeParam = urlParams.get('analysisType');
    const trackingTypeParam = urlParams.get('trackingType');
    
    console.log('🔗 Parsing URL parameters:', {
      hedwigScopeParam,
      channelParam,
      startTimeParam,
      endTimeParam,
      analysisTypeParam,
      trackingTypeParam
    });
    
    if (hedwigScopeParam) setHedwigScope(hedwigScopeParam);
    if (channelParam) setChannel(channelParam);
    if (startTimeParam) setStartTime(startTimeParam);
    if (endTimeParam) setEndTime(endTimeParam);
    if (analysisTypeParam) setAnalysisType(analysisTypeParam);
    if (trackingTypeParam) setTrackingType(trackingTypeParam as 'day' | 'week' | 'month');
  }, []);

  // Auto-trigger analysis when hedwig scope is set from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hedwigScopeParam = urlParams.get('hedwigScope');
    
    console.log('🎯 Checking auto-trigger:', {
      hedwigScopeParam,
      currentHedwigScope: hedwigScope,
      shouldTrigger: hedwigScopeParam && hedwigScope === hedwigScopeParam && hedwigScope.trim()
    });
    
    // Only auto-trigger if we have URL params and hedwig scope is now set
    if (hedwigScopeParam && hedwigScope === hedwigScopeParam && hedwigScope.trim()) {
      console.log('🔗 Auto-triggering analysis from shared link:', hedwigScope);
      handleSearch();
    }
  }, [hedwigScope]); // Trigger when hedwigScope state changes

  // Share link generation logic
  const generateShareLink = () => {
    const baseUrl = `${process.env.REACT_APP_REDIRECT_URL}/signal-to-noise`;
    const params = new URLSearchParams();
    
    if (hedwigScope.trim()) params.append('hedwigScope', hedwigScope.trim());
    if (channel && channel !== 'all') params.append('channel', channel);
    if (startTime) params.append('startTime', startTime);
    if (endTime) params.append('endTime', endTime);
    if (analysisType && analysisType !== 'hedwig-scope') params.append('analysisType', analysisType);
    if (trackingType && trackingType !== 'day') params.append('trackingType', trackingType);
    
    const shareUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
    
    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(() => {
      setSnackbarMessage('Share link copied to clipboard!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('Failed to copy link: ', err);
      setSnackbarMessage('Failed to copy link. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      // Fallback: show the link in a prompt as backup
      setTimeout(() => {
        prompt('Copy this link to share:', shareUrl);
      }, 100);
    });
  };

  // Mock data for demonstration

  const mockAlertData: AlertData[] = [
    {
      timestamp: '2024-01-01T10:00:00Z',
      alertName: 'High CPU Usage',
      severity: 'High',
      status: 'Resolved',
      duration: 45,
      source: 'Infrastructure',
      signalToNoiseRatio: 0.85
    },
    {
      timestamp: '2024-01-01T14:30:00Z',
      alertName: 'Database Connection Pool Exhausted',
      severity: 'Critical',
      status: 'Resolved',
      duration: 120,
      source: 'Database',
      signalToNoiseRatio: 0.95
    },
    {
      timestamp: '2024-01-01T16:15:00Z',
      alertName: 'Memory Leak Detected',
      severity: 'Medium',
      status: 'Active',
      duration: 30,
      source: 'Application',
      signalToNoiseRatio: 0.70
    },
    {
      timestamp: '2024-01-02T09:20:00Z',
      alertName: 'Disk Space Low',
      severity: 'Low',
      status: 'Acknowledged',
      duration: 15,
      source: 'Infrastructure',
      signalToNoiseRatio: 0.40
    },
  ];

  const mockAiInsights: AIInsight[] = [
    {
      type: 'trend',
      title: 'Alert Volume Increasing',
      description: 'Alert volume has increased by 25% compared to last month, primarily driven by infrastructure monitoring.',
      impact: 'medium',
      confidence: 87
    },
    {
      type: 'anomaly',
      title: 'Unusual Database Alert Pattern',
      description: 'Database alerts are occurring outside normal business hours, suggesting potential connection timeout issues.',
      impact: 'high',
      confidence: 92
    },
    {
      type: 'recommendation',
      title: 'Optimize Alert Thresholds',
      description: 'Consider adjusting memory usage thresholds to reduce false positives while maintaining coverage.',
      impact: 'medium',
      confidence: 78
    }
  ];

  const timeSeriesData = [
    { date: '2024-01-01', alerts: 45, signalToNoise: 0.75 },
    { date: '2024-01-02', alerts: 52, signalToNoise: 0.72 },
    { date: '2024-01-03', alerts: 38, signalToNoise: 0.82 },
    { date: '2024-01-04', alerts: 61, signalToNoise: 0.68 },
    { date: '2024-01-05', alerts: 47, signalToNoise: 0.79 },
    { date: '2024-01-06', alerts: 55, signalToNoise: 0.74 },
    { date: '2024-01-07', alerts: 42, signalToNoise: 0.84 },
  ];

  const handleSearch = async () => {
    if (!hedwigScope.trim()) {
      setError('Please enter a Hedwig scope name');
      return;
    }

    setLoading(true);
    setError(null);
    setHedwigData(null);

    try {
      // Treat input times as UTC directly - no timezone conversion needed
      // Format: "YYYY-MM-DDTHH:mm" → "YYYY-MM-DD HH:mm:ss UTC"
      const startTimeUTC = startTime.replace('T', ' ') + ':00 UTC';
      const endTimeUTC = endTime.replace('T', ' ') + ':00 UTC';

      const requestPayload = {
        scope_name: hedwigScope.trim(),
        start_time: startTimeUTC,
        end_time: endTimeUTC,
        channel: channel === 'all' ? '' : channel
      };

      console.log('🔍 Hedwig Analysis Request:', requestPayload);
      console.log('📊 Scope being sent:', hedwigScope);

      const response = await fetchHedwigAnalysis(requestPayload);

      console.log('📥 Hedwig Analysis Response:', response);
      console.log('📈 Response scope in summary:', response?.data?.summary?.analysis_scope);

      if (response.success) {
        setHedwigData(response.data);
        // Keep mock data for now for sections not yet updated to use real data
        setAlertData(mockAlertData);
        setAiInsights(mockAiInsights);
      } else {
        // Handle specific error cases
        if (response.scope_not_found) {
          setError(response.error || response.message || 'Scope not found');
        } else {
          setError(response.message || response.error || 'Analysis failed');
        }
      }
    } catch (err) {
      console.error('❌ Error in Hedwig analysis:', err);
      setError('Failed to perform analysis. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAIAnalysis = async () => {
    if (!hedwigData) {
      setAiAnalysisError('Please run a Hedwig analysis first to get alert data');
      return;
    }

    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysisResult(null);

    try {
      console.log('🤖 Starting AI analysis for scope:', hedwigData.summary?.analysis_scope);
      
      const response = await analyzeAlerts(hedwigData);
      
      if (response.success && response.data) {
        setAiAnalysisResult(response.data);
        setAiInsightsOpen(true); // Open the modal to show results
        console.log('✅ AI analysis completed successfully');
      } else {
        setAiAnalysisError(response.error || 'AI analysis failed');
      }
    } catch (err) {
      console.error('❌ Error in AI analysis:', err);
      setAiAnalysisError('Failed to perform AI analysis. Please try again.');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handlePDFExport = async () => {
    if (!hedwigData) {
      alert('No analysis data available to export. Please run analysis first.');
      return;
    }

    setPdfExportLoading(true);
    
    try {
      // Always export the main analysis content, regardless of AI insights
      await PDFExportService.exportFormattedReport(
        document.querySelector('[data-analysis-content]') as HTMLElement,
        {
          title: 'Signal to Noise Ratio Analysis Report',
          subtitle: `Hedwig Scope: ${hedwigData?.summary?.analysis_scope || 'Unknown'} | Period: ${startTime && endTime ? 
            `${new Date(startTime).toLocaleDateString()} to ${new Date(endTime).toLocaleDateString()} (${Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60 * 24))} days)` : 
            'Not set'}`,
          generatedBy: 'Maersk Reliability Framework',
          filename: aiAnalysisResult ? `snr-analysis-with-ai-${new Date().toISOString().split('T')[0]}.pdf` : `snr-analysis-report-${new Date().toISOString().split('T')[0]}.pdf`,
          maxPages: 2
        }
      );
    } catch (error) {
      console.error('PDF Export Error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  const handleAIInsightsExport = async () => {
    if (!aiAnalysisResult) {
      alert('No AI analysis data available to export. Please run AI analysis first.');
      return;
    }

    setPdfExportLoading(true);
    
    try {
      // Export the AI insights content specifically
      await PDFExportService.exportFormattedReport(
        document.querySelector('[data-ai-insights-content]') as HTMLElement,
        {
          title: 'AI Alert Analysis Report',
          subtitle: `Hedwig Scope: ${hedwigData?.summary?.analysis_scope || 'Unknown'} | Period: ${startTime && endTime ? 
            `${new Date(startTime).toLocaleDateString()} to ${new Date(endTime).toLocaleDateString()} (${Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60 * 24))} days)` : 
            'Not set'}`,
          generatedBy: 'Maersk Reliability Framework - AI Analysis',
          filename: `ai-analysis-report-${new Date().toISOString().split('T')[0]}.pdf`,
          maxPages: 1,
          orientation: 'portrait'
        }
      );
    } catch (error) {
      console.error('AI Insights PDF Export Error:', error);
      alert('Failed to export AI insights PDF. Please try again.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return '#f44336';
      case 'High': return '#ff9800';
      case 'Medium': return '#2196f3';
      case 'Low': return '#4caf50';
      default: return '#757575';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return '#f44336';
      case 'Acknowledged': return '#ff9800';
      case 'Resolved': return '#4caf50';
      default: return '#757575';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return <TrendingUp />;
      case 'anomaly': return <Warning />;
      case 'recommendation': return <CheckCircle />;
      default: return <Analytics />;
    }
  };

  // Helper functions for KPI analysis
  const getKPIStatus = (metric: string, value: string) => {
    const numValue = parseFloat(value.replace(/[^\d.]/g, ''));
    
    switch (metric) {
      case 'Mean Time To Resolution (MTTR)':
        if (numValue < 30) return { status: 'Excellent', color: '#4caf50' };
        if (numValue < 60) return { status: 'Good', color: '#4caf50' };
        if (numValue < 120) return { status: 'Moderate', color: '#ff9800' };
        return { status: 'Needs Improvement', color: '#f44336' };
      
      case 'Median Resolution Time':
        if (numValue < 20) return { status: 'Good', color: '#4caf50' };
        if (numValue < 40) return { status: 'Moderate', color: '#ff9800' };
        return { status: 'Needs Improvement', color: '#f44336' };
      
      case '95th Percentile Resolution':
        if (numValue < 60) return { status: 'Good', color: '#4caf50' };
        if (numValue < 120) return { status: 'Moderate', color: '#ff9800' };
        return { status: 'Needs Improvement', color: '#f44336' };
      
      case 'Alert Resolution Rate':
        if (numValue >= 95) return { status: 'Excellent', color: '#4caf50' };
        if (numValue >= 85) return { status: 'Good', color: '#4caf50' };
        if (numValue >= 70) return { status: 'Moderate', color: '#ff9800' };
        return { status: 'Needs Improvement', color: '#f44336' };
      
      case 'Daily Alert Frequency':
        return { status: 'Tracking', color: '#2196f3' };
      
      default:
        return { status: 'Unknown', color: '#757575' };
    }
  };

  const getKPITarget = (metric: string) => {
    switch (metric) {
      case 'Mean Time To Resolution (MTTR)': return '< 30 min';
      case 'Median Resolution Time': return '< 20 min';
      case '95th Percentile Resolution': return '< 60 min';
      case 'Alert Resolution Rate': return '> 80%';
      case 'Daily Alert Frequency': return 'Trend Analysis';
      default: return 'N/A';
    }
  };

  const getResolutionQualityAssessment = (category: string, percentage: number) => {
    const lowerCategory = category.toLowerCase();
    
    if (lowerCategory.includes('under 5') || lowerCategory.includes('5-10')) {
      if (percentage > 15) {
        return '🚨 Noise - Auto-resolved or false positive alerts (to be optimized via tuning/self-heal)';
      }
      return '🚨 Noise - Auto-resolved or false positive alerts (potential automation candidate)';
    }
    
    if (lowerCategory.includes('10-15')) {
      return '⚠️ Borderline Noise - Requires review; could indicate repetitive or minor issues';
    }

    if (lowerCategory.includes('15-30')) {
      return '✅ Healthy - Proper investigation and resolution';
    }
    
    if (lowerCategory.includes('30-60')) {
      return '🟠 Acceptable - Complex incident requiring manual intervention';
    }
    
    if (lowerCategory.includes('over 60') || lowerCategory.includes('60')) {
      return '🔴 Needs Improvement - Prolonged outages or deep-rooted issues';
    }
    
    return 'Standard Resolution Process';
  };

  const getResolutionCategoryColor = (category: string) => {
    const lowerCategory = category.toLowerCase();
    
    if (lowerCategory.includes('under 5') || lowerCategory.includes('5-10')) {
      return '#ff9800'; // Orange for potential noise
    }
    
    if (lowerCategory.includes('10-15') || lowerCategory.includes('15-30')) {
      return '#4caf50'; // Green for optimal
    }
    
    if (lowerCategory.includes('30-60')) {
      return '#ff9800'; // Yellow for acceptable
    }
    
    if (lowerCategory.includes('over 60') || lowerCategory.includes('60')) {
      return '#f44336'; // Red for needs improvement
    }
    
    return '#757575'; // Gray for others
  };

  // Calculate Signal to Noise Ratio
  const calculateSignalToNoiseRatio = (hedwigData: any) => {
    if (!hedwigData?.resolution_time_analysis?.categories || !hedwigData?.alert_type_analysis?.total_alerts) {
      return 'N/A';
    }

    const totalAlerts = hedwigData.alert_type_analysis.total_alerts;
    
    // Find alerts resolved in less than 15 minutes
    const quickResolvedAlerts = hedwigData.resolution_time_analysis.categories
      .filter((category: any) => {
        const categoryName = category.category.toLowerCase();
        return categoryName.includes('under 5') || categoryName.includes('5-10') || categoryName.includes('10-15');
      })
      .reduce((sum: number, category: any) => sum + (category.count || 0), 0);

    // Calculate actionable alerts (Total - Quick resolved)
    const actionableAlerts = totalAlerts - quickResolvedAlerts;
    
    // Calculate SNR
    const snr = totalAlerts > 0 ? (actionableAlerts / totalAlerts) : 0;
    
    return `${(snr * 100).toFixed(1)}%`;
  };

  // Function to aggregate performance data based on tracking type
  const aggregatePerformanceData = (dailyTrends: any[], type: 'day' | 'week' | 'month') => {
    if (!dailyTrends || dailyTrends.length === 0) return [];

    if (type === 'day') {
      // Return daily data as is, but format the day labels
      return dailyTrends.map(trend => ({
        ...trend,
        period: trend.day?.split('\n')[0] || trend.day, // Remove day name, keep date
        displayLabel: trend.day?.split('\n')[0] || trend.day,
        resolution_rate: trend.resolution_rate || (trend.total_alerts > 0 ? `${((trend.resolved / trend.total_alerts) * 100).toFixed(1)}%` : '0%')
      }));
    }

    if (type === 'week') {
      // Group by week
      const weeklyData: { [key: string]: any } = {};
      
      dailyTrends.forEach(trend => {
        const date = new Date(trend.day?.split('\n')[0] || trend.day);
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay()); // Get start of week (Sunday)
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            period: weekKey,
            displayLabel: `Week of ${weekStart.toLocaleDateString()}`,
            total_alerts: 0,
            resolved: 0,
            firing: 0,
            noise_alert_count: 0,
            quick_resolved_count: 0,
            days: 0
          };
        }
        
        weeklyData[weekKey].total_alerts += trend.total_alerts || 0;
        weeklyData[weekKey].resolved += trend.resolved || 0;
        weeklyData[weekKey].firing += trend.firing || 0;
        weeklyData[weekKey].noise_alert_count += trend.noise_alert_count || 0;
        weeklyData[weekKey].quick_resolved_count += trend.quick_resolved_count || 0;
        weeklyData[weekKey].days += 1;
      });
      
      // Calculate resolution rate and noise percentage for each week
      return Object.values(weeklyData).map((week: any) => ({
        ...week,
        resolution_rate: week.total_alerts > 0 ? `${((week.resolved / week.total_alerts) * 100).toFixed(1)}%` : '0%',
        noise_percentage: week.total_alerts > 0 ? `${((week.noise_alert_count / week.total_alerts) * 100).toFixed(1)}%` : '0%'
      })).sort((a: any, b: any) => a.period.localeCompare(b.period));
    }

    if (type === 'month') {
      // Group by month
      const monthlyData: { [key: string]: any } = {};
      
      dailyTrends.forEach(trend => {
        const date = new Date(trend.day?.split('\n')[0] || trend.day);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            period: monthKey,
            displayLabel: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
            total_alerts: 0,
            resolved: 0,
            firing: 0,
            noise_alert_count: 0,
            quick_resolved_count: 0,
            days: 0
          };
        }
        
        monthlyData[monthKey].total_alerts += trend.total_alerts || 0;
        monthlyData[monthKey].resolved += trend.resolved || 0;
        monthlyData[monthKey].firing += trend.firing || 0;
        monthlyData[monthKey].noise_alert_count += trend.noise_alert_count || 0;
        monthlyData[monthKey].quick_resolved_count += trend.quick_resolved_count || 0;
        monthlyData[monthKey].days += 1;
      });
      
      // Calculate resolution rate and noise percentage for each month
      return Object.values(monthlyData).map((month: any) => ({
        ...month,
        resolution_rate: month.total_alerts > 0 ? `${((month.resolved / month.total_alerts) * 100).toFixed(1)}%` : '0%',
        noise_percentage: month.total_alerts > 0 ? `${((month.noise_alert_count / month.total_alerts) * 100).toFixed(1)}%` : '0%'
      })).sort((a: any, b: any) => a.period.localeCompare(b.period));
    }

    return dailyTrends;
  };

  // Mock data calculations removed since we're using real Hedwig API data
  const avgSignalToNoise = '0.00'; // Temporary for disabled mock section

  return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
        <Grid container spacing={0} sx={{ height: '100vh' }}>
          {/* Left Sidebar - Analysis Configuration */}
          <Grid item xs={12} md={3} lg={2.5}>
            <Paper sx={{ 
              height: '100%', 
              borderRadius: 0, 
              border: 'none',
              borderRight: '1px solid #e1e5e9',
              backgroundColor: '#f8f9fa',
            }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  color: '#2c3e50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <FilterList />
                  Alert Analysis
                </Typography>

                {/* Analysis Type Selection */}
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e1e5e9' }}>
                  <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
                    Analysis Type
                  </FormLabel>
                  <RadioGroup
                    value={analysisType}
                    onChange={(e) => setAnalysisType(e.target.value)}
                  >
                    <FormControlLabel 
                      value="hedwig-scope" 
                      control={<Radio size="small" />} 
                      label={
                        <Typography variant="body2" sx={{ fontSize: '14px' }}>
                          Hedwig Analysis - Scope Information
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </Box>

                {/* Configuration Controls */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {analysisType === 'hedwig-scope' ? (
                    <TextField
                      fullWidth
                      size="small"
                      label="Hedwig Scope Name"
                      placeholder="e.g., Maersk WMS Support - APA"
                      value={hedwigScope}
                      onChange={(e) => setHedwigScope(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                        }
                      }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="Hedwig App ID"
                      value={appId}
                      onChange={(e) => setAppId(e.target.value)}
                      placeholder="e.g., A0423, A0409"
                    />
                  )}

                  {/* Channel Filter */}
                  <FormControl fullWidth size="small">
                    <InputLabel>Channel Filter</InputLabel>
                    <Select
                      value={channel}
                      label="Channel Filter"
                      onChange={(e) => setChannel(e.target.value)}
                      sx={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <MenuItem value="all">All Channels</MenuItem>
                      <MenuItem value="msteams">MS Teams</MenuItem>
                      <MenuItem value="goalert">GoAlert</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="webhook">Webhook</MenuItem>
                      <MenuItem value="snow">ServiceNow</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Date Time Pickers */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Start Time (UTC)"
                    type="datetime-local"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Enter time in UTC"
                    sx={{ fontSize: '14px' }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="End Time (UTC)"
                    type="datetime-local"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Enter time in UTC"
                    sx={{ fontSize: '14px' }}
                  />

                  {/* Analyze Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} /> : <PlayArrow />}
                    onClick={handleSearch}
                    disabled={loading}
                    sx={{ 
                      mt: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  >
                    {loading ? 'Analyzing...' : 'Analyze Alerts'}
                  </Button>
                  
                  {/* Error Display */}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  {/* AI Analysis Error Display */}
                  {aiAnalysisError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <strong>AI Analysis Error:</strong> {aiAnalysisError}
                    </Alert>
                  )}
                </Box>

                {/* Summary Stats */}
                <Box sx={{ mt: 4, p: 2, backgroundColor: '#ffffff', borderRadius: '8px' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Current Analysis
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                    <strong>Type:</strong> Hedwig Scope Analysis
                  </Typography>
                  {hedwigScope && (
                    <Typography variant="body2" sx={{ fontSize: '13px' }}>
                      <strong>Scope:</strong> {hedwigScope}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                    <strong>Channel:</strong> {channel === 'all' ? 'All Channels' : channel}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px' }}>
                    <strong>Period:</strong> {startTime && endTime ? 
                      `${new Date(startTime).toLocaleDateString()} to ${new Date(endTime).toLocaleDateString()} (${Math.round((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60 * 24))} days)` : 
                      'Not set'
                    }
                  </Typography>
                  {hedwigData && (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: '#0067b3', fontWeight: 600 }}>
                        <strong>📊 Analyzed Scope:</strong> {hedwigData.summary?.analysis_scope || 'Unknown'}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        <strong>Total Alerts:</strong> {hedwigData.alert_type_analysis?.total_alerts || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        <strong>Resolved Alerts:</strong> {hedwigData.key_performance_indicators?.context?.resolved_alerts || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '13px' }}>
                        <strong>Resolution Rate:</strong> {hedwigData.key_performance_indicators?.kpis?.["Alert Resolution Rate"] || 'N/A'}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9} lg={9.5}>
            <Box sx={{ p: 4 }} data-analysis-content>
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600,
                    color: '#2c3e50',
                    fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                  }}>
                    Hedwig Alert Insights
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {hedwigData && (
                      <MuiTooltip title="Share current analysis configuration with others">
                        <Button
                          variant="outlined"
                          startIcon={<Share />}
                          onClick={generateShareLink}
                          sx={{
                            borderColor: '#0067b3',
                            color: '#0067b3',
                            '&:hover': {
                              backgroundColor: '#e8f4fd',
                              borderColor: '#004a8a',
                            },
                          }}
                        >
                          Share Link
                        </Button>
                      </MuiTooltip>
                    )}
                    <MuiTooltip title={!hedwigData ? "Run Hedwig analysis first to get alert data" : "Analyze alerts using AI for comprehensive insights"}>
                      <span>
                        <Button
                          variant="contained"
                          startIcon={aiAnalysisLoading ? <CircularProgress size={20} color="inherit" /> : <Psychology />}
                          disabled={!hedwigData || aiAnalysisLoading}
                          onClick={handleAIAnalysis}
                          sx={{
                            background: hedwigData && !aiAnalysisLoading ? '#0067b3' : '#e0e0e0',
                            color: hedwigData && !aiAnalysisLoading ? 'white' : '#9e9e9e',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            borderRadius: 2,
                            boxShadow: hedwigData && !aiAnalysisLoading ? '0 4px 12px rgba(0, 103, 179, 0.3)' : 'none',
                            '&:hover': {
                              background: hedwigData && !aiAnalysisLoading ? '#004a8a' : '#e0e0e0',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {aiAnalysisLoading ? 'Analyzing...' : 'AI Insights'}
                        </Button>
                      </span>
                    </MuiTooltip>
                  </Box>
                </Box>
              </Box>

              {/* AI Assistant Card */}
              <Card sx={{ 
                mb: 4,
                border: '1px solid #e1e5e9',
                borderRadius: '8px',
                boxShadow: 'none',
              }}>
                <CardContent sx={{ 
                  pb: 2, 
                  borderBottom: '1px solid #e1e5e9', 
                  backgroundColor: '#e8f4fd',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SmartToy sx={{ color: '#0067b3', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: '#0067b3',
                      fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                    }}>
                      Alert Intelligence & Signal Optimization
                    </Typography>
                  </Box>
                </CardContent>
                {hedwigData ? (
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            color: '#27ae60',
                            fontSize: '28px',
                            fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                          }}>
                            {hedwigData.key_performance_indicators?.kpis?.["Alert Resolution Rate"]}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#6c757d', 
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mt: 1,
                          }}>
                            Resolution Rate
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            color: '#0067b3',
                            fontSize: '28px',
                            fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                          }}>
                            {hedwigData.alert_type_analysis?.total_alerts}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#6c757d', 
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mt: 1,
                          }}>
                            Total Alerts
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            color: '#e74c3c',
                            fontSize: '28px',
                            fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                          }}>
                            {hedwigData.key_performance_indicators?.context?.firing_alerts}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#6c757d', 
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mt: 1,
                          }}>
                            Firing Alerts
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            color: '#f39c12',
                            fontSize: '28px',
                            fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                          }}>
                            {hedwigData.key_performance_indicators?.kpis?.["Mean Time To Resolution (MTTR)"]}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#6c757d', 
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            mt: 1,
                          }}>
                            Avg Resolution Time
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ 
                            fontWeight: 700, 
                            color: '#27ae60',
                            fontSize: '28px',
                            fontFamily: "'Maersk Text', 'Inter', 'Segoe UI', Arial, sans-serif",
                          }}>
                            {calculateSignalToNoiseRatio(hedwigData)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                            <Typography variant="body2" sx={{ 
                              color: '#6c757d', 
                              fontSize: '12px',
                              fontWeight: 500,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              Signal to Noise Ratio
                            </Typography>
                            <MuiTooltip
                              title={
                                <Box sx={{ p: 1.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '14px', color: '#1a1a1a' }}>
                                    📊 Signal to Noise Ratio (SNR) Calculation
                                  </Typography>
                                  
                                  <Box sx={{ mb: 1.5, p: 1, bgcolor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '12px', color: '#1a1a1a' }}>
                                      Formula:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '12px', fontFamily: 'monospace', color: '#333' }}>
                                      SNR = Actionable Alerts ÷ Total Alerts × 100%
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ mb: 1.5, p: 1, bgcolor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: '12px', color: '#1a1a1a' }}>
                                      Actionable Alerts:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: '11px', mb: 0.5, color: '#333' }}>
                                      Total Alerts - Quick Resolved Alerts
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '10px', fontStyle: 'italic', color: '#666', display: 'block', mb: 0.5 }}>
                                      (Quick Resolved = alerts resolved in &lt; 15 minutes)
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '10px', color: '#ff9800', display: 'block', lineHeight: 1.3 }}>
                                      💡 Alerts resolved in &lt;15 min are often noise (auto-resolved, false positives, or minor issues)
                                    </Typography>
                                  </Box>
                                  
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '13px', color: '#1a1a1a' }}>
                                    📈 Rating Scale:
                                  </Typography>
                                  
                                  <Box sx={{ mb: 1, px: 1.5, py: 0.8, bgcolor: '#ffffff', borderRadius: 0.5, border: '2px solid #4caf50', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 700, fontSize: '12px' }}>
                                      &gt;70% SNR → Excellent 👏
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#2e7d32', ml: 2 }}>
                                      Very low noise
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ mb: 1, px: 1.5, py: 0.8, bgcolor: '#ffffff', borderRadius: 0.5, border: '2px solid #ff9800', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: '#f57c00', fontWeight: 700, fontSize: '12px' }}>
                                      50–70% SNR → Good 👍
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#f57c00', ml: 2 }}>
                                      Manageable quality
                                    </Typography>
                                  </Box>
                                  
                                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#ffffff', borderRadius: 0.5, border: '2px solid #f44336', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 700, fontSize: '12px' }}>
                                      &lt;50% SNR → Needs Improvement ⚠️
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '11px', color: '#d32f2f', ml: 2 }}>
                                      Too many false positives
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              arrow
                              placement="top"
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: 500,
                                  bgcolor: 'white',
                                  color: 'text.primary',
                                  border: '2px solid #0067b3',
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                                  borderRadius: 2,
                                },
                                '& .MuiTooltip-arrow': {
                                  color: 'white',
                                  '&::before': {
                                    border: '2px solid #0067b3',
                                  }
                                }
                              }}
                            >
                              <IconButton size="small" sx={{ ml: 0.5, p: 0.25 }}>
                                <Info sx={{ fontSize: 14, color: '#0067b3' }} />
                              </IconButton>
                            </MuiTooltip>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                ) : (
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Analytics sx={{ fontSize: 48, color: '#6c757d', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Ready for Analysis
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Configure your Hedwig scope and time range, then click "Analyze Alerts" to view comprehensive insights and metrics.
                    </Typography>
                  </CardContent>
                )}
              </Card>

              {/* Hedwig Analysis Results */}
              {hedwigData && (
                <>
                  {hedwigData.alert_type_analysis?.total_alerts === 0 ? (
                    <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Info sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                          No Alerts Found
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                          No alerts found for the {channel === 'all' ? 'selected scope' : `"${channel}" channel`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Scope: {hedwigScope} | Period: {startTime.replace('T', ' ')} to {endTime.replace('T', ' ')}
                        </Typography>
                      </Box>
                    </Paper>
                  ) : (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                      {/* Tabs for organized analysis sections */}
                      <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                          px: 2,
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '15px',
                            fontWeight: 600,
                            minHeight: 64,
                          },
                        }}
                      >
                        <Tab label="Alert Type Analysis" />
                        <Tab label="Key Performance Indicators" />
                        <Tab label="Resolution Time & Noise Detection" />
                        <Tab label="Performance Trends" />
                      </Tabs>

                        {/* Tab Panel 0: Alert Type Analysis */}
                        {activeTab === 0 && (
                          <Box sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                              Alert Type Analysis
                            </Typography>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Alert Type</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Resolved</TableCell>
                                    <TableCell align="right">Firing</TableCell>
                                    <TableCell align="right">% of Total</TableCell>
                                    <TableCell align="right">Avg Resolution</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {hedwigData.alert_type_analysis?.alert_types
                                    ?.slice(alertTypePage * alertTypeRowsPerPage, alertTypePage * alertTypeRowsPerPage + alertTypeRowsPerPage)
                                    ?.map((alertType: any, index: number) => (
                                    <TableRow key={index}>
                                      <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                                        {alertType.alert_type}
                                      </TableCell>
                                      <TableCell align="right">{alertType.total}</TableCell>
                                      <TableCell align="right">{alertType.resolved}</TableCell>
                                      <TableCell align="right">
                                        <Typography 
                                          sx={{ 
                                            color: alertType.firing > 0 ? '#d32f2f' : '#2e7d32',
                                            fontWeight: 600
                                          }}
                                        >
                                          {alertType.firing}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">{alertType.percentage_total}</TableCell>
                                      <TableCell align="right">{alertType.avg_resolution}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            {/* Pagination for Alert Type Analysis */}
                            <TablePagination
                              rowsPerPageOptions={[10, 25, 50]}
                              component="div"
                              count={hedwigData.alert_type_analysis?.alert_types?.length || 0}
                              rowsPerPage={alertTypeRowsPerPage}
                              page={alertTypePage}
                              onPageChange={(event, newPage) => setAlertTypePage(newPage)}
                              onRowsPerPageChange={(event) => {
                                setAlertTypeRowsPerPage(parseInt(event.target.value, 10));
                                setAlertTypePage(0);
                              }}
                              sx={{
                                borderTop: '1px solid #e0e0e0',
                                '& .MuiTablePagination-toolbar': {
                                  paddingRight: 2
                                }
                              }}
                            />
                          </Box>
                        )}

                        {/* Tab Panel 1: Key Performance Indicators */}
                        {activeTab === 1 && (
                          <Box sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Analytics sx={{ color: '#f39c12' }} />
                              Alert Metrics Deep Dive - Key Performance Indicators
                            </Typography>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Metric</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Target</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(hedwigData.key_performance_indicators?.kpis || {}).map(([key, value]) => {
                                    const statusInfo = getKPIStatus(key, value as string);
                                    const target = getKPITarget(key);
                                    
                                    return (
                                      <TableRow key={key} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                        <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                          {value as string}
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            sx={{
                                              color: statusInfo.color,
                                              fontWeight: 600,
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1
                                            }}
                                          >
                                            {statusInfo.status === 'Excellent' && <CheckCircle sx={{ color: statusInfo.color, fontSize: '18px' }} />}
                                            {statusInfo.status === 'Good' && <CheckCircle sx={{ color: statusInfo.color, fontSize: '18px' }} />}
                                            {statusInfo.status === 'Tracking' && <Analytics sx={{ color: statusInfo.color, fontSize: '18px' }} />}
                                            {(statusInfo.status === 'Needs Attention' || statusInfo.status === 'Critical') && <Warning sx={{ color: statusInfo.color, fontSize: '18px' }} />}
                                            {statusInfo.status}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontStyle: 'italic', color: '#666' }}>
                                          {target}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {/* Tab Panel 2: Resolution Time Analysis & Noise Detection */}
                        {activeTab === 2 && (
                          <Box sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SmartToy sx={{ color: '#0067b3' }} />
                              Resolution Time Analysis & Noise Detection
                            </Typography>
                            <TableContainer>
                              <Table>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Resolution Time Category</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Count</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Percentage</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Quality Assessment</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {hedwigData.resolution_time_analysis?.categories?.map((category: any, index: number) => {
                                    const categoryColor = getResolutionCategoryColor(category.category);
                                    const qualityAssessment = category.count === 0 ? 'No alerts in this category' : getResolutionQualityAssessment(category.category, parseFloat(category.percentage));
                                    const percentageNum = parseFloat(category.percentage.replace('%', ''));
                                    
                                    return (
                                      <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                                        <TableCell>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                              sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '50%',
                                                backgroundColor: categoryColor
                                              }}
                                            />
                                            <Typography sx={{ fontWeight: 500 }}>
                                              {category.category}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '16px' }}>
                                          {category.count}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                                          {category.percentage}
                                        </TableCell>
                                        <TableCell>
                                          <Typography 
                                            variant="body2" 
                                            sx={{ 
                                              color: qualityAssessment === 'No alerts in this category' ? '#757575' :
                                                     qualityAssessment.includes('Needs Improvement') ? '#f44336' :
                                                     qualityAssessment.includes('Likely Noise') || qualityAssessment.includes('Potential Noise') || qualityAssessment.includes('Acceptable') ? '#ff9800' :
                                                     qualityAssessment.includes('Optimal') ? '#4caf50' : '#757575',
                                              fontWeight: 500,
                                              fontStyle: 'italic'
                                            }}
                                          >
                                            {qualityAssessment}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            {/* Summary Insights */}
                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0067b3' }}>
                                Noise Detection Summary
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Quick resolutions (&lt; 10 min) may indicate noise or auto-resolved alerts. 
                                Prolonged resolutions (&gt; 60 min) suggest complex issues requiring investigation.
                                Optimal range: 10-30 minutes for proper analysis and resolution.
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Tab Panel 3: Performance Trends Chart */}
                        {activeTab === 3 && (
                          <Box sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUp sx={{ color: '#0067b3' }} />
                                Performance Trends Analysis
                              </Typography>
                              
                              {/* Tracking Type Selector */}
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Tracking Type</InputLabel>
                                <Select
                                  value={trackingType}
                                  label="Tracking Type"
                                  onChange={(e) => setTrackingType(e.target.value as 'day' | 'week' | 'month')}
                                >
                                  <MenuItem value="day">Daily View</MenuItem>
                                  <MenuItem value="week">Weekly View</MenuItem>
                                  <MenuItem value="month">Monthly View</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                    
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart 
                          data={aggregatePerformanceData(hedwigData.daily_performance_trends?.daily_trends || [], trackingType)}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="displayLabel" 
                            tick={{ fontSize: 11 }}
                            angle={trackingType === 'day' ? -45 : 0}
                            textAnchor={trackingType === 'day' ? 'end' : 'middle'}
                            height={trackingType === 'day' ? 80 : 60}
                            interval={0}
                          />
                          <YAxis />
                          <Tooltip 
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label) => `${trackingType.charAt(0).toUpperCase() + trackingType.slice(1)}: ${label}`}
                            content={({ active, payload, label }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Box sx={{ 
                                    backgroundColor: 'white', 
                                    p: 2, 
                                    border: '1px solid #ddd', 
                                    borderRadius: 1,
                                    boxShadow: 2
                                  }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                      {label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#0067b3', fontWeight: 600 }}>
                                      Total Alerts: {data.total_alerts}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#27ae60' }}>
                                      ✓ Resolved: {data.resolved}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#e74c3c' }}>
                                      🔥 Firing: {data.firing}
                                    </Typography>
                                    {data.noise_alert_count !== undefined && (
                                      <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2' }}>
                                        <Typography variant="body2" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                          🔕 Noise Alerts: {data.noise_alert_count} ({data.noise_percentage || 'N/A'})
                                        </Typography>
                                        {data.quick_resolved_count !== undefined && (
                                          <Typography variant="caption" sx={{ color: '#f57c00', display: 'block', mt: 0.5 }}>
                                            ⚡ Resolved in &lt;15 min: {data.quick_resolved_count}
                                          </Typography>
                                        )}
                                      </Box>
                                    )}
                                    {data.resolution_rate && (
                                      <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                                        Resolution Rate: {data.resolution_rate}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar 
                            dataKey="total_alerts" 
                            fill="url(#colorGradient)" 
                            name="Total Alerts"
                            radius={[4, 4, 0, 0]}
                            stackId="a"
                          />
                          <Bar 
                            dataKey="noise_alert_count" 
                            fill="url(#noiseGradient)" 
                            name="Noise Alerts"
                            radius={[4, 4, 0, 0]}
                            stackId="a"
                          />
                          <defs>
                            <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#0067b3" stopOpacity={0.9}/>
                              <stop offset="95%" stopColor="#004d87" stopOpacity={0.7}/>
                            </linearGradient>
                            <linearGradient id="noiseGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#ff9800" stopOpacity={0.9}/>
                              <stop offset="95%" stopColor="#f57c00" stopOpacity={0.7}/>
                            </linearGradient>
                          </defs>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                    
                            {/* Chart Legend */}
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 3, pb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 16, height: 16, backgroundColor: '#0067b3', borderRadius: 1 }} />
                                <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                                  Total Alerts
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 16, height: 16, backgroundColor: '#ff9800', borderRadius: 1 }} />
                                <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                                  Noise Alerts
                                </Typography>
                              </Box>
                            </Box>

                            {/* Performance Summary */}
                            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0067b3' }}>
                                {trackingType === 'day' ? 'Daily' : trackingType === 'week' ? 'Weekly' : 'Monthly'} Performance Summary
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                {trackingType === 'day' && 'Daily trends show alert volume patterns including noise alerts (auto-resolved/low-value alerts). Hover over bars to see detailed breakdown of total alerts, resolved, firing, and noise alerts with percentages.'}
                                {trackingType === 'week' && 'Weekly aggregation shows total alert volumes and noise patterns across business cycles. Hover over bars for detailed breakdown including noise alert counts.'}
                                {trackingType === 'month' && 'Monthly view reveals long-term alert volume and noise trends. Hover over bars for resolution details, firing alerts, and noise alert analysis.'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                    </Paper>
                  )}
                </>
              )}

      {false && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card className="metric-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Total Alerts</Typography>
                  <Typography variant="h3" color="primary">{alertData.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className="metric-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Avg Signal/Noise</Typography>
                  <Typography variant="h3" color="primary">{avgSignalToNoise}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className="metric-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Critical Alerts</Typography>
                  <Typography variant="h3" color="error">
                    {alertData.filter(a => a.severity === 'Critical').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card className="metric-card">
                <CardContent>
                  <Typography variant="h6" gutterBottom>Avg Duration</Typography>
                  <Typography variant="h3" color="primary">
                    {Math.round(alertData.reduce((sum, alert) => sum + alert.duration, 0) / alertData.length)}m
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* AI Insights Section */}
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy color="primary" />
              AI-Powered Insights
            </Typography>
            <Grid container spacing={2}>
              {aiInsights.map((insight, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      borderLeft: `4px solid ${insight.impact === 'high' ? '#f44336' : insight.impact === 'medium' ? '#ff9800' : '#4caf50'}`
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getInsightIcon(insight.type)}
                        <Typography variant="subtitle1" fontWeight="bold">
                          {insight.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {insight.description}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          label={`${insight.impact.toUpperCase()} IMPACT`} 
                          size="small"
                          color={insight.impact === 'high' ? 'error' : insight.impact === 'medium' ? 'warning' : 'success'}
                        />
                        <Typography variant="caption">
                          {insight.confidence}% confidence
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Alert Volume Trend</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="alerts" stroke="#1976d2" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Signal to Noise Ratio</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={timeSeriesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 1]} />
                    <Tooltip formatter={(value) => [`${(Number(value) * 100).toFixed(1)}%`, 'Signal/Noise']} />
                    <Legend />
                    <Area type="monotone" dataKey="signalToNoise" stroke="#4caf50" fill="#4caf50" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Alert Details Table */}
          <Paper elevation={2}>
            <Typography variant="h6" sx={{ p: 2 }}>Alert Details</Typography>
            <TableContainer className="scrollable-content">
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Alert Name</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Duration (min)</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="right">Signal/Noise</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alertData.map((alert, index) => (
                    <TableRow key={index}>
                      <TableCell>{new Date(alert.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{alert.alertName}</TableCell>
                      <TableCell>
                        <Chip
                          label={alert.severity}
                          sx={{ 
                            backgroundColor: getSeverityColor(alert.severity), 
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={alert.status}
                          sx={{ 
                            backgroundColor: getStatusColor(alert.status), 
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell>{alert.duration}</TableCell>
                      <TableCell>{alert.source}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {alert.signalToNoiseRatio > 0.8 ? (
                            <TrendingUp color="success" />
                          ) : (
                            <TrendingDown color="error" />
                          )}
                          {(alert.signalToNoiseRatio * 100).toFixed(1)}%
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Advanced Analysis Section */}
          <Accordion sx={{ mt: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Advanced Analysis & Recommendations</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Pattern Detection</Typography>
                    <Typography variant="body2">
                      AI has detected recurring patterns in your alert data that suggest potential optimization opportunities.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Threshold Optimization</Typography>
                    <Typography variant="body2">
                      Current thresholds may be generating 23% more noise than optimal. Consider adjustment.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </>
      )}
            </Box>
          </Grid>
        </Grid>

        {/* AI Insights Modal */}
        <Dialog
          open={aiInsightsOpen}
          onClose={() => setAiInsightsOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white', 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            pb: 2 
          }}>
            <Psychology sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                🤖 AI-Powered Alert Analysis & Recommendations
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                Intelligent insights based on your alert patterns and performance metrics
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3, maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
            {aiAnalysisResult ? (
              <div data-ai-insights-content>
                <AIInsightsDisplay 
                  analysisResult={aiAnalysisResult}
                  loading={aiAnalysisLoading}
                />
              </div>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Psychology sx={{ fontSize: 64, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  No AI analysis available
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Please run an AI analysis to see insights here.
                </Typography>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e1e5e9' }}>
            <Button
              onClick={() => setAiInsightsOpen(false)}
              variant="outlined"
              sx={{ 
                borderColor: '#0067b3',
                color: '#0067b3',
                '&:hover': {
                  borderColor: '#004a8a',
                  backgroundColor: 'rgba(0, 103, 179, 0.04)'
                }
              }}
            >
              Close
            </Button>
            {hedwigData && aiAnalysisResult && (
              <Button
                variant="contained"
                startIcon={pdfExportLoading ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
                disabled={pdfExportLoading}
                onClick={handleAIInsightsExport}
                sx={{
                  background: pdfExportLoading ? '#e0e0e0' : 'linear-gradient(135deg, #0067b3 0%, #004a8a 100%)',
                  color: pdfExportLoading ? '#9e9e9e' : 'white',
                  fontWeight: 600,
                  '&:hover': {
                    background: pdfExportLoading ? '#e0e0e0' : 'linear-gradient(135deg, #004a8a 0%, #003366 100%)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {pdfExportLoading ? 'Generating PDF...' : 'Export AI Analysis Report'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      {/* Snackbar for share link feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignalToNoiseRatio;
